import React, { useRef } from "react";
import cx from "classnames";
import PropTypes from "prop-types";
import ReactModal from "react-modal";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import { Icon } from "../Icons";
import * as styles from "./Modal.module.scss";

const ModalSizes = {
	condensed: "condensed",
	expanded: "expanded",
};

const defaultLabels = {
	content: "Modal",
	close: "Close",
};

const Modal = ({
	appElement,
	children,
	className,
	classNames,
	labels: providedLabels,
	size,
	mobileFullScreen,
	isOpen,
	handleClose,
	shouldCloseOnOverlayClick,
	shouldCloseOnEsc,
	isDismissible,
	withoutContentWrapper,
	...reactModalProps
}) => {
	const labels = {
		...defaultLabels,
		...providedLabels,
	};

	const contentElRef = useRef();
	const handleAfterOpen = () => {
		disableBodyScroll(contentElRef.current, {
			reserveScrollBarGap: true,
		});
	};

	const handleAfterClose = () => {
		// Handle cases where body scroll isn't enabled when closing the modal
		if (contentElRef.current) {
			enableBodyScroll(contentElRef.current);
		}
	};

	const handleCloseModal = () => {
		if (contentElRef.current) {
			enableBodyScroll(contentElRef.current);
		}
		if (typeof handleClose === "function") {
			handleClose();
		}
	};

	return (
		<ReactModal
			appElement={appElement}
			className={cx(className, styles.modal, {
				[styles[size]]: styles[size],
			})}
			overlayClassName={cx(
				styles.overlay,
				{ [styles.mobileFullScreen]: mobileFullScreen },
				classNames?.overlay
			)}
			contentLabel={labels.content}
			isOpen={isOpen}
			onAfterOpen={handleAfterOpen}
			onAfterClose={handleAfterClose}
			onRequestClose={handleCloseModal}
			contentRef={element => {
				if (element) {
					contentElRef.current = element;
				}
			}}
			shouldCloseOnOverlayClick={isDismissible && shouldCloseOnOverlayClick}
			shouldCloseOnEsc={isDismissible && shouldCloseOnEsc}
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...reactModalProps}>
			{isDismissible && (
				<button
					type="button"
					className={cx(styles.closeButton, classNames?.closeButton)}
					onClick={handleCloseModal}>
					{labels.close}
					<Icon
						name="close"
						className={cx(styles.closeIcon, classNames?.closeIcon)}
						title={labels.close}
					/>
				</button>
			)}
			{withoutContentWrapper ? (
				children
			) : (
				<div className={cx(styles.content, classNames?.content)}>{children}</div>
			)}
		</ReactModal>
	);
};

Modal.propTypes = {
	/**
	 * Expects either undefined or a DOM element
	 */
	appElement: PropTypes.object,
	children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
	className: PropTypes.string,
	classNames: PropTypes.shape({
		overlay: PropTypes.string,
		closeButton: PropTypes.string,
		closeIcon: PropTypes.string,
		content: PropTypes.string,
	}),
	labels: PropTypes.shape({
		content: PropTypes.string.isRequired,
		close: PropTypes.string,
	}),
	size: PropTypes.oneOf(Object.keys(ModalSizes)),
	mobileFullScreen: PropTypes.bool,
	isOpen: PropTypes.bool.isRequired,
	handleClose: PropTypes.func,
	shouldCloseOnOverlayClick: PropTypes.bool,
	shouldCloseOnEsc: PropTypes.bool,
	/**
	 * Combines both shouldCloseOnOverlayClick and shouldCloseOnEsc
	 */
	isDismissible: PropTypes.bool,
	/**
	 * Renders the children without a wrapper div
	 */
	withoutContentWrapper: PropTypes.bool,
};

Modal.defaultProps = {
	appElement: undefined,
	className: undefined,
	classNames: undefined,
	labels: defaultLabels,
	size: ModalSizes.expanded,
	mobileFullScreen: false,
	handleClose: undefined,
	shouldCloseOnOverlayClick: true,
	shouldCloseOnEsc: true,
	isDismissible: true,
	withoutContentWrapper: false,
};

export { Modal, ModalSizes };
